import HomeIcon from "@mui/icons-material/Home";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import SailingIcon from "@mui/icons-material/Sailing";
import ScheduleIcon from "@mui/icons-material/Schedule";
import { Box, Chip, CircularProgress, Divider, Stack, SxProps, Typography } from "@mui/material";
import { useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Stock, StockEditionsEnum, StockUnitEnum } from "../../api";
import { routes } from "../../constants/routes";
import {
  designOptions,
  editionColorMapping,
  editionFontMapping,
  editionOptions,
  surfaceOptions,
  technicalOptions,
  typeOptions
} from "../../constants/stock-attributes";
import { GoogleServicesContext } from "../../context/GoogleServicesContext";
import { UserContext } from "../../context/UserContext";
import { colors } from "../../theme/colors";
import { gaAction, gaCategory } from "../../utils/analytics";
import { useGradient } from "../../utils/useGradient";
import { formatPrice, formatStock, getImagePath, roundNumber } from "../../utils/utilities";
import { ButtonSize, ButtonVariant } from "../utils/Button";
import NavButton from "../utils/NavButton";

const ProductCard = ({
  product,
  sx = {},
  onNavigation
}: {
  product: Stock;
  sx?: SxProps;
  onNavigation?: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const { analyticsEvent } = useContext(GoogleServicesContext);
  const gradient = useGradient("vertical");

  const { user } = useContext(UserContext);

  const type = useMemo(() => typeOptions(t), [t, i18n.language]);
  const edition = useMemo(() => editionOptions(t), [t, i18n.language]);
  const technical = useMemo(() => technicalOptions(t), [t, i18n.language]);
  const surface = useMemo(() => surfaceOptions(t), [t, i18n.language]);
  const design = useMemo(() => designOptions(t), [t, i18n.language]);

  const priceToUse = product.priceForCurrentUser ?? product.price ?? 0;

  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const productRoute = `${routes.products}/${product.ean}`;

  const unit =
    product.unit === StockUnitEnum.Meter ? t("product.meterUnit") : t("product.pieceUnit");

  const productQuantityUnit = roundNumber(
    (product.packagesOnStock ?? 0) * (product.amountPerPackage ?? 0)
  );

  const quantityUnit =
    product.unit === StockUnitEnum.Meter
      ? t("product.meterUnit")
      : !product.packagesOnStock
      ? t("product.pieceUnit3")
      : product.packagesOnStock === 1
      ? t("product.pieceUnit")
      : product.packagesOnStock >= 2 && product.packagesOnStock < 5
      ? t("product.pieceUnit2")
      : t("product.pieceUnit3");

  const isPreparing = product.editions?.includes(StockEditionsEnum.Preparing);
  const hasStock = user ? productQuantityUnit > 0 : !!product.inStock;

  const stock = isPreparing
    ? t("products.preparing")
    : !hasStock
    ? t("products.noStock")
    : user
    ? `${formatStock(productQuantityUnit)} ${quantityUnit}`
    : t("products.inStock");

  const stockIcon = isPreparing ? (
    <ScheduleIcon sx={{ mr: 2 }} titleAccess={stock} />
  ) : hasStock ? (
    <HomeIcon sx={{ mr: 2 }} titleAccess={stock} />
  ) : (
    <SailingIcon sx={{ mr: 2 }} titleAccess={stock as string} />
  );

  // We want to filter out the "ks" part of the name
  const productName =
    product.name
      ?.split(" ")
      .filter((part) => part !== "ks")
      .join(" ") ?? "";

  const tileImagePath = product?.tileFaceImages?.[0];

  const handleNavigationClick = () => {
    onNavigation?.();
    analyticsEvent(gaCategory.product, gaAction.click);
  };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        minWidth: { xs: theme.spacing(45), md: theme.spacing(50) },
        width: { xs: "100%", sm: theme.spacing(60) },
        maxWidth: theme.spacing(70),
        borderRadius: theme.spacing(4),
        background: theme.palette.mode === "light" ? colors.white : colors.black,
        overflow: "hidden",
        boxShadow: "0px 0px 10px 3px rgba(0, 0, 0, 0.25)",
        m: 2,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...(sx as any)
      })}>
      <Link
        tabIndex={-1}
        aria-hidden="true"
        to={productRoute}
        style={{ textDecoration: "none", position: "relative" }}
        onClick={handleNavigationClick}>
        {tileImagePath && !imageError ? (
          <Box
            sx={(theme) => ({
              position: "relative",
              height: { xs: theme.spacing(45), md: theme.spacing(50) },
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center"
            })}>
            <Box
              onError={() => setImageError(true)}
              onLoad={() => setImageLoaded(true)}
              component="img"
              loading="lazy"
              src={getImagePath(tileImagePath)}
              alt={product.name}
              sx={{
                filter: imageLoaded ? "none" : "blur(20px)",
                transition: "filter 0.3s ease"
              }}
            />
            {!imageLoaded && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center"
                }}>
                <CircularProgress
                  size={32}
                  sx={(theme) => ({
                    color: theme.palette.mode === "light" ? colors.black : colors.white
                  })}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={(theme) => ({
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: { xs: theme.spacing(25), md: theme.spacing(40) }
            })}>
            <ImageNotSupportedIcon
              titleAccess={t("buttons.imageNotAvailable")}
              sx={(theme) => ({
                fontSize: { xs: theme.spacing(5), md: theme.spacing(10) },
                mt: 5,
                color: colors.brown
              })}
            />
          </Box>
        )}

        <Stack
          sx={{ position: "absolute", top: 0, right: 0, p: 2 }}
          direction={{ xs: "column", md: "row" }}
          spacing={1}>
          {product.editions
            ?.filter(
              (value) =>
                !(
                  value === StockEditionsEnum.ClearanceSale &&
                  product.editions?.includes(StockEditionsEnum.Discontinued)
                )
            )
            .map((value) =>
              value === StockEditionsEnum.ClearanceSale ? StockEditionsEnum.Discontinued : value
            )
            .map((value) => (
              <Chip
                sx={{
                  backgroundColor: editionColorMapping[value],
                  color: editionFontMapping[value],
                  mb: 1,
                  cursor: "pointer",
                  boxShadow:
                    "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)"
                }}
                key={value}
                label={edition.find((opt) => opt.value === value)?.label}
              />
            ))}

          {product.individualPriceForCurrentUser && (
            <Chip
              sx={{
                p: { xs: 1, md: 2 },
                m: 1,
                cursor: "pointer",
                borderRadius: 10,
                boxShadow:
                  "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)",
                backgroundColor: colors.orange,
                color: colors.black
              }}
              label={t("products.individualPrice")}
            />
          )}
        </Stack>
      </Link>
      <Box
        p={3}
        sx={{
          display: "flex",
          height: "100%",
          flexDirection: "column",
          justifyContent: "center"
        }}>
        <Typography variant="h5" textAlign="center" fontWeight="bold" mb={2}>
          {productName}
        </Typography>

        {/* THICKNESS AND COLORS ARE NOT SHOWN AS CHIPS FOR NOW */}
        {/* <Stack sx={{ width: "100%", justifyContent: "center" }} direction="row" spacing={1} mb={4}>
          {product.thickness && (
            <Chip
              sx={{
                boxShadow:
                  "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)"
              }}
              label={thicknessOptionsMap[product.thickness]}
            />
          )}
          {product.colors?.map((value) => (
            <Chip
              sx={{
                backgroundColor: colorMapping[value],
                backgroundImage:
                  (value as StockColorsEnum) === StockColorsEnum.Other
                    ? "conic-gradient(red 0deg 120deg, #009f05 120deg 240deg, blue 240deg 360deg)"
                    : "none",
                color: colorFontMapping[value],
                boxShadow:
                  "0px 2px 1px -1px rgba(0,0,0,0.2), 0px 1px 1px 0px rgba(0,0,0,0.14), 0px 1px 3px 0px rgba(0,0,0,0.12)"
              }}
              key={value}
              label={color.find((opt) => opt.value === value)?.label}
            />
          ))}
        </Stack> */}

        {[
          ...(product.types ?? []),
          ...(product.technicalParameters ?? []),
          ...(product.surfaces ?? []),
          ...(product.designs ?? [])
        ].length > 0 && <Divider sx={{ width: "100%", mb: 3 }} />}

        {product.types && product.types.length > 0 && (
          <Stack direction="row" alignItems="center" spacing={2} mb={2}>
            <PlayArrowOutlinedIcon aria-hidden="true" />
            <Typography variant="body1" fontWeight="bold">
              {product.types.map((t) => type.find((opt) => opt.value === t)?.label).join(", ")}
            </Typography>
          </Stack>
        )}

        {product.technicalParameters && product.technicalParameters.length > 0 && (
          <Stack direction="row" spacing={2} mb={2}>
            <PlayArrowOutlinedIcon aria-hidden="true" />
            <Typography variant="body1" fontWeight="bold">
              {product.technicalParameters
                .map((t) => technical.find((opt) => opt.value === t)?.label)
                .join(", ")}
            </Typography>
          </Stack>
        )}

        {((product.surfaces && product.surfaces.length > 0) ||
          (product.designs && product.designs.length > 0)) && (
          <Stack direction="row" spacing={2} mb={2}>
            <PlayArrowOutlinedIcon aria-hidden="true" />
            <Typography variant="body1" fontWeight="bold">
              {product.surfaces &&
                product.surfaces.length > 0 &&
                product.surfaces
                  .map((t) => surface.find((opt) => opt.value === t)?.label)
                  .join(", ")}

              {product.designs && product.designs.length > 0 && (
                <>
                  {product.surfaces && product.surfaces.length > 0 && ", "}
                  {product.designs
                    .map((t) => design.find((opt) => opt.value === t)?.label)
                    .join(", ")}
                </>
              )}
            </Typography>
          </Stack>
        )}

        <Box
          sx={{
            flex: 1,
            width: "100%",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-end",
            alignItems: "center"
          }}>
          <Divider sx={{ width: "100%", my: 2 }} />

          <Typography
            fontWeight="bold"
            textAlign="center"
            variant="h4"
            sx={{
              background: gradient,
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              fontWeight: "bold",
              mb: 2
            }}>
            {priceToUse > 0 ? formatPrice(priceToUse) : "-"} / {unit}
            {/* {priceToUse > 0 ? `${formatPrice(priceToUse)} / ${unit}` : "-"} */}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center" }}>
            {stockIcon}
            <Typography
              variant="body2"
              fontWeight="bold"
              textAlign="center"
              sx={{ fontWeight: 300 }}>
              {stock}
            </Typography>
          </Box>
        </Box>

        <NavButton
          onClick={handleNavigationClick}
          route={productRoute}
          label={t("buttons.detail")}
          variant={ButtonVariant.primary}
          size={ButtonSize.full}
          sx={{ mt: 3 }}
        />
      </Box>
    </Box>
  );
};

export default ProductCard;
